import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { adminApiClient } from "../../services/apiClient";
import { toApiError } from "../../types/api";

import type { AdminDashboardOverview } from "@lingoshade/domain";

interface AdminStatsState {
  stats: AdminDashboardOverview | null;
  loading: boolean;
  error: string | null;
}

const initialState: AdminStatsState = {
  stats: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchAdminStats = createAsyncThunk<
  AdminDashboardOverview,
  void,
  { rejectValue: string }
>(
  "adminStats/fetchStats",
  async (
    _,
    { rejectWithValue },
  ): Promise<AdminDashboardOverview | ReturnType<typeof rejectWithValue>> => {
    try {
      const response =
        await adminApiClient.get<AdminDashboardOverview>("/admin/dashboard");
      return response.data;
    } catch (error: unknown) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const adminStatsSlice = createSlice({
  name: "adminStats",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearStats: (state) => {
      state.stats = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchAdminStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearStats } = adminStatsSlice.actions;

export default adminStatsSlice.reducer;
